import type { PlatformAdapter, PlatformEvent, PlatformName } from '../adapter';

type StubName = Exclude<PlatformName, 'standalone'>;

export interface StubLogEntry {
  method: string;
  args: unknown[];
  at: number;
}

const MAX_LOG = 200;
const logs = new Map<StubName, StubLogEntry[]>();

function record(name: StubName, method: string, args: unknown[]): void {
  let list = logs.get(name);
  if (!list) {
    list = [];
    logs.set(name, list);
  }
  list.push({ method, args, at: Date.now() });
  if (list.length > MAX_LOG) list.shift();
}

export function getStubCallLog(name: StubName): readonly StubLogEntry[] {
  return logs.get(name) ?? [];
}

export function resetStubCallLog(): void {
  logs.clear();
}

/** Lifecycle-order machine. One per adapter instance. */
interface LifecycleAsserter {
  note(method: string): void;
}

function createAsserter(name: StubName): LifecycleAsserter {
  let loadingComplete = false;
  let gameplayActive = false;

  const warn = (expected: string, method: string) =>
    console.warn(
      `[platform:${name}:stub] order violation: ${method} called but expected ${expected} first`,
      expected,
    );

  return {
    note(method) {
      switch (method) {
        case 'loadingComplete':
          loadingComplete = true;
          break;
        case 'gameplayStart':
          if (!loadingComplete) warn('loadingComplete', method);
          if (gameplayActive) warn('gameplayStop', method);
          gameplayActive = true;
          break;
        case 'gameplayStop':
          gameplayActive = false;
          break;
        case 'showInterstitial':
        case 'showRewarded':
          if (gameplayActive) warn('gameplayStop', method);
          break;
      }
    },
  };
}

export function makeStubAdapter(name: StubName): PlatformAdapter {
  const listeners: Map<PlatformEvent, Set<() => void>> = new Map();
  const asserter = createAsserter(name);
  const log = (method: string, ...args: unknown[]) => {
    record(name, method, args);
    asserter.note(method);
    if (typeof console.debug === 'function') {
      console.debug(`[platform:${name}:stub]`, method, ...args);
    }
  };

  return {
    name,
    async init({ gameId, version }) {
      log('init', { gameId, version });
    },
    loadingStart() {
      log('loadingStart');
    },
    loadingProgress(f) {
      log('loadingProgress', f);
    },
    loadingComplete() {
      log('loadingComplete');
    },
    gameplayStart() {
      log('gameplayStart');
    },
    gameplayStop() {
      log('gameplayStop');
    },
    happyTime(intensity) {
      log('happyTime', intensity ?? 1);
    },
    showInterstitial: async () => {
      log('showInterstitial');
      return { shown: false };
    },
    showRewarded: async () => {
      log('showRewarded');
      return { completed: true };
    },
    isAdBlocked: () => false,
    async saveData(key, value) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        log('saveData err', e);
      }
    },
    async loadData<T>(key: string, defaultValue: T): Promise<T> {
      try {
        const raw = localStorage.getItem(key);
        return raw === null ? defaultValue : (JSON.parse(raw) as T);
      } catch {
        return defaultValue;
      }
    },
    trackEvent(n, p) {
      log('event', n, p ?? {});
    },
    capabilities: {
      hasOwnPauseButton: false,
      hasOwnMuteButton: false,
      hasOwnFullscreen: false,
      supportsHaptics: false,
      showsBranding: false,
    },
    on(event, handler) {
      let set = listeners.get(event);
      if (!set) {
        set = new Set();
        listeners.set(event, set);
      }
      set.add(handler);
      return () => listeners.get(event)?.delete(handler);
    },
  };
}
